/**
 * Idempotency keys for the two POSTs an upload takes.
 *
 * ── ONE KEY PER OPERATION, AND THEY ARE NOT INTERCHANGEABLE ─────────────────
 *
 * `POST /uploads` and `POST /recordings/jobs` each get their own stable key.
 * Reusing one across both is a **409 by construction**: the backend's
 * `application/idempotency.py` conflicts when the stored *endpoint* differs as
 * well as when the body does. That is deliberate on its side and must not be
 * discovered on ours.
 *
 * ── WHY A NEW FILE MEANS NEW KEYS ───────────────────────────────────────────
 *
 * This is the part that is easy to get wrong, and it fails silently.
 * `UploadRequest` carries **only** `user_id`, and the backend fingerprints the
 * request body — so for `POST /uploads` the fingerprint is effectively constant
 * per user. The key is therefore the only thing distinguishing one upload
 * operation from another, and reusing it does not conflict: it **replays the
 * original grant**, whichever file the user has picked since, and however long
 * ago that grant expired. There is no error anywhere in that sequence.
 *
 * So §2.8's "a new file, condition, or expired grant starts an explicit new
 * operation" is not hygiene. It is the only thing standing between a second
 * attempt and the first attempt's dead grant.
 *
 * A *transport retry* is the opposite case and must reuse both keys unchanged:
 * that is what makes a timed-out `POST /recordings/jobs` recover the Job it
 * already created instead of creating a second one that appends the same
 * recording to the baseline twice.
 */

export interface OperationKeys {
  /** For `POST /uploads`. */
  readonly upload: string
  /** For `POST /recordings/jobs`. A DIFFERENT value, never the one above. */
  readonly job: string
}

/**
 * Mint the pair for one upload operation.
 *
 * Called when the user picks a file, changes the condition, or restarts after an
 * expired grant — and at no other time. Everything in between is a retry of an
 * operation that already has its keys.
 */
export function newOperationKeys(): OperationKeys {
  return { upload: crypto.randomUUID(), job: crypto.randomUUID() }
}

/**
 * Whether two file selections are the same operation.
 *
 * Name, size and last-modified. Not the bytes: hashing a session archive in the
 * browser to decide whether to mint a UUID would be minutes of main-thread work
 * to answer a question whose wrong answer costs one wasted grant.
 */
export function sameSelection(a: File | null, b: File | null): boolean {
  if (a === null || b === null) return a === b
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified
}
